/**
 * Body Composition (BC): a vital of its own, separate from Body Weight (HS). Every value is a
 * dimension with its own threshold, but the reading raises one alert, as Blood Pressure does
 * for sys/dia: the alert is configured once, on the weight, and copied to the other values.
 */
export const BODY_COMPOSITION_VITAL = 'BC';

/**
 * Whether a SKU can be used for a vital group. A SKU's `type` names one group, but the scales
 * (HS2S, BYOD / Kiosk weighing scale) are typed HS and also serve Body Composition: the backend
 * maps them to the BC vital def, which reaches us in `vitalDefsMapping`. Only BC is shared this
 * way, so other devices mapped to several defs (BP with HR) still match by type alone.
 */
export const skuServesVital = (sku, vitalName) =>
  !!sku &&
  (sku.type === vitalName ||
    (vitalName === BODY_COMPOSITION_VITAL &&
      (sku.vitalDefsMapping || []).some((vitalDef) => vitalDef.name === BODY_COMPOSITION_VITAL)));

/**
 * The vital groups a programme form can offer. While Body Composition is not released (the session's
 * `bodyCompositionEnabled`) it cannot be added, but a programme that already has it keeps it.
 */
export const selectableVitals = (vitalsConfig, programmeVitals, bodyCompositionEnabled) =>
  bodyCompositionEnabled || programmeVitals.some((vital) => vital.vitalGroupName === BODY_COMPOSITION_VITAL)
    ? vitalsConfig
    : vitalsConfig.filter((vital) => vital.value !== BODY_COMPOSITION_VITAL);

/** The weight: always measured, and the dimension that holds the alert settings. */
export const BODY_COMPOSITION_PRIMARY_DIMENSION = 'bcWeight';

/** In display order; matches the backend's vital_dimension ids 13-21. */
export const BODY_COMPOSITION_DIMENSIONS = [
  'bcWeight',
  'bcBmi',
  'bcBodyFat',
  'bcBodyWater',
  'bcMuscleMass',
  'bcLeanMass',
  'bcBoneMass',
  'bcVisceralFat',
  'bcBmr',
];

export const BODY_COMPOSITION_SECONDARY_DIMENSIONS = BODY_COMPOSITION_DIMENSIONS.filter(
  (name) => name !== BODY_COMPOSITION_PRIMARY_DIMENSION,
);

/** The masses, which take the country's weight unit (lbs in the US) like Body Weight does. */
export const BODY_COMPOSITION_MASS_DIMENSIONS = ['bcWeight', 'bcMuscleMass', 'bcLeanMass', 'bcBoneMass'];

/** A BC value whose alert follows the weight's, so it shows no alert control of its own. */
export const isSecondaryBodyCompositionDimension = (name) => BODY_COMPOSITION_SECONDARY_DIMENSIONS.includes(name);

/** Vital groups enrolled with the patient's height, weight and BMI type. */
export const usesPatientBmi = (vitalGroupName) => vitalGroupName === 'HS' || vitalGroupName === BODY_COMPOSITION_VITAL;

/** True for a Body Composition reading (its values are components, like Blood Pressure's). */
/* A missed task is stored with the nine dimensions but no values (dataAbsentReason "Not Performed");
   it is not a reading, so it falls through to the "Missed" display every other vital uses. */
export const isBodyCompositionReading = (observation) =>
  Boolean(observation) &&
  !observation.dataAbsentReason &&
  ((observation.vitals || []).includes(BODY_COMPOSITION_VITAL) ||
    (observation.component || []).some((c) => c && c.code && c.code.text === BODY_COMPOSITION_PRIMARY_DIMENSION));

/** The value of one dimension in a reading, with its unit and grade; null when not measured. */
export const bodyCompositionValue = (observation, name) => {
  const component = ((observation && observation.component) || []).find((c) => c && c.code && c.code.text === name);
  if (!component || !component.valueQuantity) return null;
  return {
    value: component.valueQuantity.value,
    unit: component.valueQuantity.unit,
    interpretation:
      component.interpretation && component.interpretation.coding && component.interpretation.coding[0]
        ? component.interpretation.coding[0].code
        : null,
  };
};

/** The values the reading actually has, in display order; values the scale did not produce are left out. */
export const measuredBodyCompositionValues = (observation) =>
  BODY_COMPOSITION_DIMENSIONS.map((name) => ({ name, measured: bodyCompositionValue(observation, name) }))
    .filter(({ measured }) => measured)
    .map(({ name, measured }) => ({ name, ...measured }));

/** How many measured values fall outside their bands. */
export const countOutOfRange = (observation) =>
  BODY_COMPOSITION_DIMENSIONS.map((name) => bodyCompositionValue(observation, name)).filter(
    (v) => v && v.interpretation && v.interpretation !== 'N',
  ).length;

/** Whole numbers for the level and kcal/day, two decimals for kg, one decimal place for the rest. */
export const formatBodyCompositionValue = (name, value, unit) => {
  if (name === 'bcVisceralFat' || name === 'bcBmr') return Math.round(Number(value)).toLocaleString('en-US');
  return Number(value).toFixed(unit === 'kg' ? 2 : 1);
};

/** Translation key (under listItemDetail.bodyComposition) for each dimension's label. */
export const BODY_COMPOSITION_LABEL_KEYS = {
  bcWeight: 'bodyWeight',
  bcBmi: 'bmi',
  bcBodyFat: 'bodyFat',
  bcBodyWater: 'bodyWater',
  bcMuscleMass: 'muscleMass',
  bcLeanMass: 'leanMass',
  bcBoneMass: 'boneMass',
  bcVisceralFat: 'visceralFat',
  bcBmr: 'bmr',
};

/** Units as people read them. */
export const displayBodyCompositionUnit = (unit) => (unit === 'kg/m2' ? 'kg/m²' : unit === 'level' ? '' : unit || '');

const DISPLAY_NAMES = {
  bcWeight: 'Body Weight',
  bcBmi: 'BMI',
  bcBodyFat: 'Body Fat',
  bcBodyWater: 'Body Water',
  bcMuscleMass: 'Muscle Mass',
  bcLeanMass: 'Lean Mass',
  bcBoneMass: 'Bone Mass',
  bcVisceralFat: 'Visceral Fat',
  bcBmr: 'BMR',
};

const SEVERITY_RANK = { HH: 2, LL: 2, H: 1, L: 1 };

/**
 * A Body Composition reading as one line for lists (alerts, tasks): its out-of-range values,
 * most severe first, e.g. "Body Fat 41.0 %, BMI 31.2 kg/m²"; the weight when all are in range.
 */
export const bodyCompositionReadingText = (observation) => {
  const measured = BODY_COMPOSITION_DIMENSIONS.map((name) => ({ name, ...bodyCompositionValue(observation, name) })).filter(
    (v) => v.value !== undefined,
  );
  const breached = measured
    .filter((v) => v.interpretation && v.interpretation !== 'N')
    .sort((a, b) => (SEVERITY_RANK[b.interpretation] || 0) - (SEVERITY_RANK[a.interpretation] || 0));
  const shown = breached.length ? breached : measured.filter((v) => v.name === BODY_COMPOSITION_PRIMARY_DIMENSION);
  return shown
    .map((v) => `${DISPLAY_NAMES[v.name]} ${formatBodyCompositionValue(v.name, v.value, v.unit)} ${displayBodyCompositionUnit(v.unit)}`.trim())
    .join(', ');
};
